// **Τι είδους πόρος είναι αυτός;** — ΜΙΑ διατύπωση, για όλη την εφαρμογή.
//
// ⚠ **ΔΕΝ ΕΙΝΑΙ ΤΟ ΙΔΙΟ ΜΕ ΤΗΝ ΠΡΟΕΛΕΥΣΗ, ΚΑΙ ΤΑ ΔΥΟ ΣΥΝΥΠΑΡΧΟΥΝ.** Η κάρτα
// δείχνει ήδη εικονίδιο **προέλευσης** —YouTube, Wikipedia, Google Drive— που
// απαντά «**ποιος ιστότοπος**». Εδώ απαντιέται «**τι είδος περιεχομένου**»:
// βίντεο, ήχος, σύνδεσμος. Τα δύο δεν συμπίπτουν και δεν αντικαθιστά το ένα το
// άλλο: ένας σύνδεσμος Google Drive μπορεί να είναι βίντεο, και ένα βίντεο
// μπορεί να μη φιλοξενείται σε γνωστό ιστότοπο.
//
// ⚠ **ΚΑΜΙΑ ΑΠΟΘΗΚΕΥΣΗ, ΚΑΝΕΝΑ ΠΕΔΙΟ ΣΤΟ ΜΟΝΤΕΛΟ — ΚΑΙ ΕΙΝΑΙ ΑΠΟΦΑΣΗ.**
// Πεδίο `type` στον πόρο θα έπρεπε να γεμίζεται από **τρεις** πελάτες (πόρταλ,
// επέκταση, τρίτα συστήματα μέσω API), δηλαδή θα ήταν τρεις διατυπώσεις του ίδιου
// κανόνα — το μοτίβο που έχει ήδη κοστίσει έξι φορές. Και θα **πάγωνε**: πόρος
// καταχωρημένος σήμερα ως «σύνδεσμος» θα έμενε έτσι αν αύριο ο κανόνας μάθαινε να
// αναγνωρίζει τη μορφή του.
//
// Ο τύπος είναι **παράγωγο της διεύθυνσης**, όχι δεδομένο του πόρου: υπολογίζεται
// κάθε φορά, από ένα σημείο, και βελτιώνεται για **όλους** τους υπάρχοντες πόρους
// μόλις ο κανόνας βελτιωθεί.
//
// **Τι ΔΕΝ κάνει, γραμμένο πριν από τον κώδικα:** δεν κατεβάζει τίποτα και δεν
// ρωτά κανέναν. Κρίνει **μόνο** από τη διεύθυνση. Άρα σελίδα που *περιέχει*
// βίντεο —άρθρο με ενσωματωμένο παίκτη— μετριέται ως **σύνδεσμος**, σωστά: ο
// πόρος είναι η σελίδα, όχι το βίντεο μέσα της.

/** Οι τέσσερις τιμές. Κλειστό σύνολο, και το `other` είναι πραγματική απάντηση. */
export const RESOURCE_KINDS = Object.freeze(['video', 'audio', 'link', 'other']);

/**
 * Ιστότοποι που **είναι** πλατφόρμες βίντεο, ανεξάρτητα από τη μορφή του αρχείου.
 *
 * Απαριθμούνται γιατί δεν συνάγονται: το `youtube.com/watch?v=…` δεν έχει καμία
 * κατάληξη που να το προδίδει. Ο κατάλογος είναι **σκόπιμα μικρός** — καλύπτει
 * ό,τι συναντά η πιλοτική και όχι το διαδίκτυο.
 */
const VIDEO_HOSTS = ['youtube.com', 'youtu.be', 'vimeo.com', 'dailymotion.com'];

/** Το ίδιο για τον ήχο. */
const AUDIO_HOSTS = ['spotify.com', 'soundcloud.com', 'anchor.fm', 'podbean.com'];

/**
 * Καταλήξεις αρχείων. **Χωρίς τελεία στην αρχή**, ώστε η σύγκριση να είναι μία.
 *
 * Το `mp4` καλύπτει και το `m4v`· δεν προστίθενται μορφές που κανείς δεν
 * καταχωρεί, γιατί κάθε επιπλέον γραμμή είναι κάτι που πρέπει να συντηρηθεί.
 */
const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'webm', 'mov', 'avi', 'mkv', 'ogv'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'flac', 'aac'];

/** Ο κεντρικός τομέας, χωρίς `www.` και χωρίς υποτομείς που δεν χρειάζονται. */
function hostOf(url) {
    try {
        return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    } catch {
        return '';
    }
}

/** Η κατάληξη της διαδρομής, χωρίς παραμέτρους και χωρίς άγκυρα. */
function extensionOf(url) {
    try {
        const path = new URL(url).pathname.toLowerCase();
        const match = path.match(/\.([a-z0-9]{1,5})$/);
        return match ? match[1] : '';
    } catch {
        return '';
    }
}

/**
 * Το είδος του πόρου, από τη διεύθυνσή του.
 *
 * **Η σειρά των ελέγχων είναι μέρος του κανόνα:** η κατάληξη αρχείου προηγείται
 * του ιστότοπου, γιατί είναι **ισχυρότερη ένδειξη** — ένα `.mp3` σε πλατφόρμα
 * βίντεο είναι ήχος. Και οι δύο προηγούνται του γενικού «σύνδεσμος».
 *
 * @returns μία από τις `RESOURCE_KINDS`. Ποτέ `null`: διεύθυνση που δεν
 *   αναγνωρίζεται είναι **σύνδεσμος**, και διεύθυνση που δεν διαβάζεται καθόλου
 *   είναι **άλλο** — η διάκριση μετράει, γιατί η δεύτερη σημαίνει «κάτι δεν πάει
 *   καλά με τα δεδομένα» και δεν πρέπει να κρύβεται μέσα στη συνηθισμένη τιμή.
 */
export function kindOf(url) {
    const text = String(url ?? '').trim();
    if (text === '') return 'other';

    const host = hostOf(text);
    if (host === '') return 'other';

    const extension = extensionOf(text);
    if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
    if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';

    const matches = (hosts) => hosts.some((known) => host === known || host.endsWith(`.${known}`));
    if (matches(VIDEO_HOSTS)) return 'video';
    if (matches(AUDIO_HOSTS)) return 'audio';

    return 'link';
}

export default { kindOf, RESOURCE_KINDS };
